(() => {
    if (window.__poPlpVariantFilterInitialized) {
        return;
    }
    window.__poPlpVariantFilterInitialized = true;

    const normalizeHandle = (handle) => (handle || "").toString().trim().toLowerCase();
    const getScopedCollectionHandle = () => {
        const currentHandle = normalizeHandle(window.collection?.handle);
        const plpHandle = normalizeHandle(window.collection?.plp_handle);

        if (!currentHandle) {
            return "";
        }

        // Always scope to the canonical collection handle so that markets using
        // the same English handle (e.g. en-gb on "polarized-sunglasses") are
        // filtered the same way as markets with a localised handle.
        // When handles differ the canonical plpHandle is used so the
        // data-variant-collection-handles attribute (always canonical) still matches.
        return plpHandle || currentHandle;
    };
    const getCollectionHandlesForElement = (element) =>
        String(element?.dataset?.variantCollectionHandles || "")
            .split(",")
            .map((value) => normalizeHandle(value))
            .filter(Boolean);

    const getGroupsFromWindow = () => {
        const groupsByKey = window.__poLastPLPGroupsByKey;
        if (!(groupsByKey instanceof Map) || groupsByKey.size === 0) {
            return [];
        }

        return Array.from(groupsByKey.entries()).map(([xGroupKey, hits]) => ({
            xGroupKey,
            hits: Array.isArray(hits) ? hits : [],
        }));
    };

    const getGroupsFromStorage = () => {
        const windowGroups = getGroupsFromWindow();
        if (windowGroups.length) {
            return windowGroups;
        }

        try {
            const raw = sessionStorage.getItem("lastPLPGroups");
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (_e) {
            return [];
        }
    };

    const clearStoredGroupsCache = () => {
        window.__poLastPLPGroupsByKey = new Map();

        try {
            sessionStorage.setItem("lastPLPGroups", "[]");
        } catch (_e) {
            // no-op
        }
    };

    let cachedGroups = [];
    let cachedFiltersKey = "";
    let lastFacetFilters = null;
    let cachedAllowedHandlesByHandle = new Map();
    let cachedAllowedHandlesByGroupKey = new Map();

    const normalizeFacetFilters = (facetFilters) => {
        if (!facetFilters) return [];
        if (Array.isArray(facetFilters)) return facetFilters;
        if (typeof facetFilters === "string") {
            try {
                const parsed = JSON.parse(facetFilters);
                return Array.isArray(parsed) ? parsed : [];
            } catch (_e) {
                return [];
            }
        }
        return [];
    };

    const buildFiltersKey = (filters) => {
        try {
            return JSON.stringify(filters || []);
        } catch (_e) {
            return "[]";
        }
    };

    const getCurrentFacetFilters = () => {
        if (Array.isArray(lastFacetFilters)) return lastFacetFilters;
        const raw = window.algoliaShopify?.instantsearch?.search?.helper?.state?.facetFilters;
        return normalizeFacetFilters(raw);
    };

    const hasActiveFilters = () => {
        const filters = getCurrentFacetFilters();
        if (!filters.length) return false;
        return filters.some((entry) =>
            Array.isArray(entry) ? entry.some(Boolean) : Boolean(entry)
        );
    };

    const buildAllowedHandlesMaps = (groups = []) => {
        const byHandle = new Map();
        const byGroupKey = new Map();
        const scopedCollectionHandle = getScopedCollectionHandle();

        (groups || []).forEach((group) => {
            const groupKey = normalizeHandle(group?.xGroupKey);
            const hits = Array.isArray(group?.hits) ? group.hits : [];
            const collectionScopedHits = scopedCollectionHandle
                ? hits.filter((hit) => {
                    const collectionHandles = Array.isArray(hit?.collections)
                        ? hit.collections.map((value) => normalizeHandle(value))
                        : [];
                    return collectionHandles.includes(scopedCollectionHandle);
                })
                : [];
            const effectiveHits = collectionScopedHits.length > 0
                ? collectionScopedHits
                : hits;
            const uniqueHandles = Array.from(
                new Set(
                    effectiveHits
                        .map((hit) => normalizeHandle(hit?.handle))
                        .filter(Boolean)
                )
            );

            // Merge variant handles from rendered slides in DOM to ensure PLP/PDP parity.
            // The server renders all metafield variants as slides; Algolia groups may be incomplete.
            if (groupKey) {
                const card = document.querySelector(
                    `.po-algolia-product-card[data-group-key="${groupKey}"]`
                ) || document.querySelector(
                    `po-plp-product-card[data-group-key="${groupKey}"]`
                );
                if (card) {
                    const slides = card.querySelectorAll("[data-variant-handle]");
                    const handleSet = new Set(uniqueHandles);
                    slides.forEach((slide) => {
                        const h = normalizeHandle(slide.dataset.variantHandle);
                        if (h && !handleSet.has(h)) {
                            uniqueHandles.push(h);
                            handleSet.add(h);
                        }
                    });
                }
            }

            if (!uniqueHandles.length) {
                return;
            }

            if (groupKey && !byGroupKey.has(groupKey)) {
                byGroupKey.set(groupKey, uniqueHandles);
            }

            uniqueHandles.forEach((handle) => {
                if (!byHandle.has(handle)) {
                    byHandle.set(handle, uniqueHandles);
                }
            });
        });

        return { byHandle, byGroupKey };
    };

    const updateAllowedHandlesCache = (groups = []) => {
        const { byHandle, byGroupKey } = buildAllowedHandlesMaps(groups);
        cachedAllowedHandlesByHandle = byHandle;
        cachedAllowedHandlesByGroupKey = byGroupKey;
    };

    const getAllowedHandles = (handle, groupKey) => {
        const target = normalizeHandle(handle);
        if (target && cachedAllowedHandlesByHandle.has(target)) {
            return cachedAllowedHandlesByHandle.get(target) || null;
        }

        const normalizedGroupKey = normalizeHandle(groupKey);
        if (normalizedGroupKey && cachedAllowedHandlesByGroupKey.has(normalizedGroupKey)) {
            return cachedAllowedHandlesByGroupKey.get(normalizedGroupKey) || null;
        }

        return null;
    };

    const updateCounters = (slider, count) => {
        slider.querySelectorAll(".slider-counter--total").forEach((el) => {
            el.textContent = String(count);
        });

        slider
            .querySelectorAll(".slider-counter--current, .slider-counter-current--mobile")
            .forEach((el) => {
                el.textContent = "1";
            });
    };

    const setItemVisibility = (element, isVisible) => {
        if (!element) return;
        if (isVisible) {
            element.style.removeProperty("display");
            element.removeAttribute("data-po-hidden");
            return;
        }
        element.style.display = "none";
        element.setAttribute("data-po-hidden", "1");
    };

    const reorderElementsByAllowedOrder = (elements, getHandle, allowedHandles) => {
        if (!Array.isArray(elements) || elements.length < 2 || !(allowedHandles instanceof Set) || allowedHandles.size === 0) {
            return;
        }

        const orderMap = new Map(Array.from(allowedHandles).map((handle, index) => [handle, index]));
        const orderedElements = elements
            .map((element, originalIndex) => {
                const handle = normalizeHandle(getHandle(element));
                return {
                    element,
                    originalIndex,
                    order: orderMap.has(handle) ? orderMap.get(handle) : Number.MAX_SAFE_INTEGER,
                };
            })
            .sort((left, right) => {
                if (left.order !== right.order) {
                    return left.order - right.order;
                }

                return left.originalIndex - right.originalIndex;
            });

        orderedElements.forEach(({ element }) => element.parentElement?.appendChild(element));
    };

    const shouldHideVariantCarousel = (card) => {
        if (!card) return false;

        const collectionNotGrouped = window.collection?.not_grouped;
        if (
            collectionNotGrouped === true
            || collectionNotGrouped === "true"
            || collectionNotGrouped === 1
            || collectionNotGrouped === "1"
        ) {
            return true;
        }

        return Boolean(
            card.querySelector('po-plp-product-card[data-hide-variant-carousel="true"]')
        );
    };

    const setSliderUiVisibility = (card, isVisible) => {
        if (!card) return;
        const hideVariantCarousel = shouldHideVariantCarousel(card);
        const elements = card.querySelectorAll(
            ".mobile_button_slider, .po-product-card-controller, .po-product-card-thumbnails-wrapper, .po-product-card-thumbnails, [data-controller], [data-thumbnails]"
        );
        elements.forEach((el) => setItemVisibility(el, hideVariantCarousel ? false : isVisible));
    };

    const refreshSlider = (slider, count) => {
        slider.sliderItems = slider.querySelectorAll('[id^="Slide-"]:not([data-po-hidden])');
        slider.thumbnailItems = slider.querySelectorAll('[id^="Thumbnail-"]:not([data-po-hidden])');
        slider.thumbnailWrapper = slider.querySelector(".slider-thumbnails");
        if (slider.slider) {
            slider.slider.scrollLeft = 0;
        }

        if (typeof slider.resetPages === "function") {
            slider.resetPages();
        }

        const disableNav = count <= 1;
        if (slider.prevButton) {
            slider.prevButton.toggleAttribute("disabled", disableNav);
        }
        if (slider.nextButton) {
            slider.nextButton.toggleAttribute("disabled", disableNav);
        }
        if (slider.prevButtonMobile) {
            slider.prevButtonMobile.toggleAttribute("disabled", disableNav);
        }
        if (slider.nextButtonMobile) {
            slider.nextButtonMobile.toggleAttribute("disabled", disableNav);
        }

        if (slider.thumbnailItems?.length) {
            slider.thumbnailItems.forEach((thumb, idx) => {
                thumb.dataset.selected = idx === 0 ? "true" : "false";
            });
        }

        if (typeof slider.updateSelectedThumbnail === "function") {
            slider.updateSelectedThumbnail(0);
        }
        if (typeof slider.setSlidePosition === "function") {
            slider.setSlidePosition(0, "auto");
        } else if (slider.slider) {
            slider.slider.scrollLeft = 0;
        }
    };

    const resetCardVariants = (card) => {
        const slider = card.querySelector("po-slider-component");
        if (!slider) return;

        const slides = Array.from(slider.querySelectorAll("[data-variant-handle]"));
        const thumbs = Array.from(slider.querySelectorAll("[data-current-variant]"));
        if (!slides.length || !thumbs.length) return;

        slides.forEach((slide) => setItemVisibility(slide, true));
        thumbs.forEach((thumb) => setItemVisibility(thumb, true));

        updateCounters(slider, slides.length);
        refreshSlider(slider, slides.length);
        setSliderUiVisibility(card, slides.length > 1);
    };

    const filterCardVariants = (card) => {
        const handle = normalizeHandle(card?.dataset?.laDnProductHandle);
        const groupKey = normalizeHandle(card?.dataset?.groupKey);
        if (!handle && !groupKey) return;

        const slider = card.querySelector("po-slider-component");
        if (!slider) return;

        const slides = Array.from(slider.querySelectorAll("[data-variant-handle]"));
        const thumbs = Array.from(slider.querySelectorAll("[data-current-variant]"));
        if (!slides.length || !thumbs.length) return;

        const allowedHandles = getAllowedHandles(handle, groupKey);
        const scopedCollectionHandle = getScopedCollectionHandle();
        const canonicalHandle = normalizeHandle(window.collection?.plp_handle);
        const localHandle = normalizeHandle(window.collection?.handle);
        
        if (!scopedCollectionHandle) {
            return;
        }
        
        const collectionScopedHandles = new Set();
        
        slides.forEach((slide) => {
            const variantHandle = normalizeHandle(slide.dataset.variantHandle);
            if (!variantHandle) return;
        
            const collectionHandles = getCollectionHandlesForElement(slide);
            if (
                collectionHandles.includes(scopedCollectionHandle) ||
                (canonicalHandle && collectionHandles.includes(canonicalHandle)) ||
                (localHandle && collectionHandles.includes(localHandle))
            ) {
                collectionScopedHandles.add(variantHandle);
            }
        });

        // If no slides match the scoped collection (e.g. localized handle mismatch),
        // do not filter — keep all server-rendered slides visible.
        if (collectionScopedHandles.size === 0) {
            return;
        }

        let allowed = allowedHandles && allowedHandles.length > 0
            ? new Set(allowedHandles.map(normalizeHandle))
            : null;

        if (collectionScopedHandles.size > 0) {
            allowed = allowed
                ? new Set(Array.from(allowed).filter((variantHandle) => collectionScopedHandles.has(variantHandle)))
                : collectionScopedHandles;
        }

        if (!allowed || allowed.size === 0) return;

        const existingHandles = new Set(
            slides
                .map((slide) => normalizeHandle(slide.dataset.variantHandle))
                .filter(Boolean)
        );

        const hasIntersection = Array.from(existingHandles).some((h) => allowed.has(h));
        if (!hasIntersection) return;

        reorderElementsByAllowedOrder(slides, (slide) => slide.dataset.variantHandle, allowed);
        reorderElementsByAllowedOrder(thumbs, (thumb) => thumb.dataset.currentVariant, allowed);

        slides.forEach((slide) => {
            const isAllowed = allowed.has(normalizeHandle(slide.dataset.variantHandle));
            setItemVisibility(slide, isAllowed);
        });

        thumbs.forEach((thumb) => {
            const isAllowed = allowed.has(normalizeHandle(thumb.dataset.currentVariant));
            setItemVisibility(thumb, isAllowed);
        });

        const remaining = slider.querySelectorAll("[data-variant-handle]:not([data-po-hidden])").length;
        if (remaining === 0) return;

        updateCounters(slider, remaining);
        refreshSlider(slider, remaining);
        setSliderUiVisibility(card, remaining > 1);
    };

    const applyToCard = (card, groups, filtersActive) => {
        if (!card) {
            return;
        }

        const hasGroupContext = Array.isArray(groups) && groups.length > 0;

        if (!filtersActive && !hasGroupContext) {
            if (card.querySelector("[data-po-hidden]")) {
                resetCardVariants(card);
            }
            return;
        }

        if (!hasGroupContext) {
            return;
        }

        filterCardVariants(card);
    };

    const applyToAllCards = () => {
        const filtersActive = hasActiveFilters();
        const groups = cachedGroups.length ? cachedGroups : getGroupsFromStorage();

        if (groups.length && cachedAllowedHandlesByHandle.size === 0 && cachedAllowedHandlesByGroupKey.size === 0) {
            updateAllowedHandlesCache(groups);
        }

        const scope = document.querySelector(".ais-Hits-list") || document;
        scope.querySelectorAll(".po-algolia-product-card").forEach((card) => {
            applyToCard(card, groups, filtersActive);
        });
    };

    let applyScheduled = false;
    const pendingCards = new Set();

    const scheduleApply = (card = null) => {
        if (card) {
            pendingCards.add(card);
        }

        if (applyScheduled) return;
        applyScheduled = true;
        requestAnimationFrame(() => {
            applyScheduled = false;
            if (pendingCards.size === 0) {
                applyToAllCards();
                return;
            }

            const filtersActive = hasActiveFilters();
            const groups = cachedGroups.length ? cachedGroups : getGroupsFromStorage();

            if (groups.length && cachedAllowedHandlesByHandle.size === 0 && cachedAllowedHandlesByGroupKey.size === 0) {
                updateAllowedHandlesCache(groups);
            }

            Array.from(pendingCards).forEach((pendingCard) => {
                if (pendingCard?.isConnected) {
                    applyToCard(pendingCard, groups, filtersActive);
                }
            });

            pendingCards.clear();
        });
    };

    document.addEventListener("plp:groups-loaded", (event) => {
        const groups = event?.detail;
        cachedGroups = Array.isArray(groups) ? groups : getGroupsFromStorage();
        updateAllowedHandlesCache(cachedGroups);
        lastFacetFilters = normalizeFacetFilters(
            window.algoliaPoCustomSearch?.activeFacetFilters || getCurrentFacetFilters()
        );
        cachedFiltersKey = buildFiltersKey(lastFacetFilters);
        scheduleApply();
    });

    document.addEventListener("plp:filters-changed", (event) => {
        lastFacetFilters = normalizeFacetFilters(event?.detail?.facetsFilters);
        cachedGroups = [];
        cachedFiltersKey = "";
        cachedAllowedHandlesByHandle = new Map();
        cachedAllowedHandlesByGroupKey = new Map();
        clearStoredGroupsCache();
        scheduleApply();
    });

    const handleCardRendered = (event) => {
        const storedGroups = cachedGroups.length ? cachedGroups : getGroupsFromStorage();
        if (!hasActiveFilters() && !storedGroups.length) {
            return;
        }

        if (!cachedGroups.length) {
            cachedGroups = storedGroups;
            updateAllowedHandlesCache(cachedGroups);
        }

        const card = event?.detail?.card;
        if (card instanceof Element) {
            scheduleApply(card);
            return;
        }

        scheduleApply();
    };

    // plp:card-rendered is dispatched by po-algolia-search.js after each card is
    // inserted into the DOM (fetchProductCard) or restored from the virtual DOM
    // (remountVirtualizedCardSlot).
    document.addEventListener("plp:card-rendered", handleCardRendered);

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            scheduleApply();
        });
    } else {
        scheduleApply();
    }
})();
